import React from 'react';
import cx from 'classnames';
import { cardClass } from '@app/constants/styles';

/**
 * The surfaces a dashboard section is built from: a label naming the section, the card the figures sit on,
 * and the one-figure card. Shared rather than owned by Insights — the client product tabs and the product
 * page want the same ones, and a second copy is how two dashboards start looking like different products.
 */

const SECTION_LABEL = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

/** An `action` sits on the label's line, at the far end, and stays off paper. */
export const SectionLabel = ({
  children,
  action,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
}): React.ReactElement =>
  action ? (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
      <p className={SECTION_LABEL}>{children}</p>
      <div className="print:hidden">{action}</div>
    </div>
  ) : (
    <p className={cx('mb-3', SECTION_LABEL)}>{children}</p>
  );

const SECTION_HEADING = 'text-lg font-bold text-foreground';

/** A tab's own heading, as the product page's money tabs title their year. Same API as `SectionLabel`. */
export const SectionHeading = ({
  children,
  action,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
}): React.ReactElement =>
  action ? (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <h2 className={SECTION_HEADING}>{children}</h2>
      <div className="print:hidden">{action}</div>
    </div>
  ) : (
    <h2 className={cx('mb-4', SECTION_HEADING)}>{children}</h2>
  );

export const Card = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement => <div className={cx('break-inside-avoid', cardClass, className)}>{children}</div>;

export const StatCard = ({ stat }: { stat: { label: string; value: string; sub: string } }): React.ReactElement => (
  <Card>
    <p className="text-2xl font-bold text-foreground">{stat.value}</p>
    <p className="mt-1 text-sm font-medium text-foreground">{stat.label}</p>
    <p className="text-xs text-muted-foreground">{stat.sub}</p>
  </Card>
);
